/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useState } from "react";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { getAssets, SBulkImportAssets } from "../../../apis/app.service";
import AppButton from "../../sharedcomponents/Buttons/AppButton/AppButton";
import LoadingSpinner from "../../sharedcomponents/LoadingSpinner";
import { formatDate } from "../../../helpers/FormatDate";
import { useAssetsPage } from "../../../store/AssetsProvider";
import { IAsset, IAssetForm, EMPTY_ASSET_FORM } from "../core/models";
import { downloadAssetQr, downloadAssetsQrZip } from "../core/qr";
import { parseAssetsFromExcel } from "../core/excel";
import CreateEditAsset from "./CreateEditAsset";
import AssignAsset from "./AssignAsset";
import ConfirmAssetDelete from "./ConfirmAssetDelete";
import AssetQrModal from "./AssetQrModal";
import AssetQrFieldsModal from "./AssetQrFields";
import ManageCategories from "./ManageCategories";
import "../core/styles.scss";

function assetToForm(row: IAsset): IAssetForm {
  return {
    id: row._id,
    category: row.category || "Other",
    assetNo: row.assetNo || "",
    status: row.status || "Functional",
    description: row.description || "",
    serialNumber: row.serialNumber || "",
    supplier: row.supplier || "",
    purchaseDate: row.purchaseDate ? row.purchaseDate.slice(0, 10) : "",
    totalCost: row.totalCost != null ? String(row.totalCost) : "",
    currency: row.currency || "UGX",
    notes: row.notes || "",
  };
}

const statusClass = (status: string) =>
  `asset-status asset-status--${(status || "other").toLowerCase()}`;

export default function AssetWrapper() {
  const { setAsset, setActiveAsset, qrFields } = useAssetsPage();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [zipping, setZipping] = useState(false);

  const { data, isLoading, isError, error } = useQuery<IAsset[]>({
    queryKey: ["assets"],
    queryFn: () => getAssets(),
  });

  const importMutation = useMutation({
    mutationFn: SBulkImportAssets,
    onSuccess: ({ data: summary, message }) => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast.success(
        `${message}: ${summary.created} added, ${summary.updated} updated, ${summary.skipped} skipped.`,
        { duration: 5000, position: "top-right" },
      );
    },
    onError: (err: Error) =>
      toast.error(err.message, { duration: 4000, position: "top-right" }),
  });

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    try {
      const assets = await parseAssetsFromExcel(file);
      if (assets.length === 0) {
        toast.error(
          "No valid rows found. Ensure an 'Asset No' column exists.",
          {
            position: "top-right",
          },
        );
        return;
      }
      importMutation.mutate(assets);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to read the file.", {
        position: "top-right",
      });
    }
  };

  const handleDownloadZip = async (assets: IAsset[]) => {
    if (assets.length === 0) return;
    setZipping(true);
    try {
      const count = await downloadAssetsQrZip(assets, qrFields);
      toast.success(`Bundled ${count} QR code(s) into a ZIP.`, {
        position: "top-right",
      });
    } catch {
      toast.error("Could not build the ZIP file.", { position: "top-right" });
    } finally {
      setZipping(false);
    }
  };

  const openQr = (row: IAsset) => {
    setActiveAsset(row);
  };

  const columns = [
    {
      name: "assetNo",
      label: "Asset No",
      options: { filter: false, sort: true },
    },
    {
      name: "category",
      label: "Category",
      options: { filter: true, sort: true },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (v: string) => (
          <span className="asset-cell-truncate" title={v}>
            {v}
          </span>
        ),
      },
    },
    {
      name: "serialNumber",
      label: "Serial No",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v: string) => <span>{v || "—"}</span>,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        setCellProps: () => ({ style: { textAlign: "center" } }),
        customBodyRender: (v: string) => (
          <span className={statusClass(v)}>{v || "—"}</span>
        ),
      },
    },
    {
      name: "assignedTo",
      label: "Assigned To",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) =>
          value?.name ? (
            <span>
              {value.name}
              {value.department ? (
                <small className="asset-assignee-dept">
                  {" "}
                  · {value.department}
                </small>
              ) : null}
            </span>
          ) : (
            <span className="asset-unassigned">Unassigned</span>
          ),
      },
    },
    {
      name: "purchaseDate",
      label: "Purchased",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (v: string) => <span>{v ? formatDate(v) : "—"}</span>,
      },
    },
    {
      name: "",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: false,
        setCellHeaderProps: () => ({ style: { textAlign: "center" } }),
        setCellProps: () => ({ style: { textAlign: "center" } }),
        // customBodyRenderLite gives the index into the original `data` array
        // (stable across sorting/pagination), unlike customBodyRender's
        // tableMeta.tableData, which holds transformed cell values, not the
        // original row objects.
        customBodyRenderLite: (dataIndex: number) => {
          const row = (data || [])[dataIndex];
          if (!row) return null;
          return (
            <div className="asset-actions">
              <span
                className="asset-icon-btn"
                title="Edit"
                onClick={() =>
                  setAsset({ action: "edit", data: assetToForm(row) })
                }
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_create_asset"
              >
                <i className="fas fa-edit"></i>
              </span>
              <span
                className="asset-icon-btn"
                title="Assign"
                onClick={() => {
                  setActiveAsset(row);
                  setAsset({ action: "assign", data: assetToForm(row) });
                }}
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_assign_asset"
              >
                <i className="fas fa-user-tag"></i>
              </span>
              <span
                className="asset-icon-btn"
                title="QR code"
                onClick={() => openQr(row)}
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_asset_qr"
              >
                <i className="fas fa-qrcode"></i>
              </span>
              <span
                className="asset-icon-btn"
                title="Download QR"
                onClick={() => downloadAssetQr(row, qrFields)}
              >
                <i className="fas fa-download"></i>
              </span>
              <span
                className="asset-icon-btn asset-icon-btn--danger"
                title="Delete"
                onClick={() =>
                  setAsset({ action: "delete", data: assetToForm(row) })
                }
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_confirm_asset_delete"
              >
                <i className="fas fa-trash"></i>
              </span>
            </div>
          );
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    filterType: "dropdown",
    viewColumns: true,
    responsive: "standard",
    selectableRows: "multiple",
    jumpToPage: true,
    print: false,
    download: false,
    elevation: 1,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100],
    customToolbar: () => (
      <>
        <AppButton
          btnType="secondary"
          data-bs-toggle="modal"
          data-bs-target="#kt_modal_manage_categories"
          style={{ width: "140px", marginRight: "10px" }}
        >
          <i className="fa-solid fa-tags me-2"></i>
          <span>Categories</span>
        </AppButton>
        <AppButton
          btnType="info"
          disabled={importMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
          style={{ width: "150px", marginRight: "10px" }}
        >
          <i className="fa-solid fa-file-import me-2"></i>
          <span>Import Excel</span>
        </AppButton>
        <AppButton
          btnType="secondary"
          data-bs-toggle="modal"
          data-bs-target="#kt_modal_asset_qr_fields"
          title="Choose which details are encoded into generated QR codes"
          style={{ width: "140px", marginRight: "10px" }}
        >
          <i className="fa-solid fa-sliders me-2"></i>
          <span>QR Fields</span>
        </AppButton>
        <AppButton
          btnType="primary"
          disabled={zipping || !data || data.length === 0}
          onClick={() => handleDownloadZip(data || [])}
          style={{ width: "160px", marginRight: "10px" }}
        >
          <i className="fa-solid fa-file-zipper me-2"></i>
          <span>{zipping ? "Preparing…" : "All QR (ZIP)"}</span>
        </AppButton>
        <AppButton
          btnType="success"
          data-bs-toggle="modal"
          data-bs-target="#kt_modal_create_asset"
          onClick={() =>
            setAsset({ action: "create", data: { ...EMPTY_ASSET_FORM } })
          }
          style={{ width: "140px", marginRight: "10px" }}
        >
          <i className="fa-solid fa-plus me-2"></i>
          <span>Add Asset</span>
        </AppButton>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: "none" }}
          onChange={handleImportFile}
        />
      </>
    ),
    customToolbarSelect: (selectedRows) => {
      const selected = (selectedRows.data as { dataIndex: number }[])
        .map((r) => (data ? data[r.dataIndex] : null))
        .filter(Boolean) as IAsset[];
      return (
        <div className="asset-select-toolbar">
          <AppButton
            btnType="primary"
            width="190px"
            disabled={zipping}
            onClick={() => handleDownloadZip(selected)}
          >
            <i className="fa-solid fa-download me-2"></i>
            <span>
              {zipping ? "Preparing…" : `Download ${selected.length} QR (ZIP)`}
            </span>
          </AppButton>
        </div>
      );
    },
  };

  if (isLoading) return <LoadingSpinner />;

  if (isError) {
    return (
      <div className="w-75 mr-auto p-4">
        <p>Oops! Something went wrong loading assets.</p>
        <p>{(error as Error)?.message}</p>
      </div>
    );
  }

  return (
    <div className="assets">
      <MUIDataTable
        title={"Asset Register"}
        data={data || []}
        columns={columns}
        options={options}
      />

      <CreateEditAsset />
      <AssignAsset />
      <ConfirmAssetDelete />
      <AssetQrModal />
      <AssetQrFieldsModal />
      <ManageCategories />
    </div>
  );
}
